import { useState } from 'react'

import { reasonRefusal, reasonRemaining, serialiseReason } from '@/lib/data/reasoncode'
import type { DecideBlock, DecidePanel } from '../spec/types'

/**
 * The judgement composer — the console's first and only write.
 *
 * # What this block refuses to make easy
 *
 * Everything here is shaped by one sentence in the design brief: **`allow` is
 * required and has no default**, because a body omitting it reads as `false` and
 * `false` retires somebody's name for ever. So:
 *
 *   - No radio is pre-selected. `outcome` starts as `''`, which is not one of
 *     the options, and there is no code path that sets it to anything but a
 *     value the operator clicked.
 *   - The two outcomes are drawn as two equally weighted controls, side by side,
 *     with each one's consequence written under it. There is no primary button
 *     and no autofocus — a default-focused control that commits on Enter is a
 *     default by another name.
 *   - Submit is dead until an outcome AND a reason code have been chosen, and
 *     the sentence beside it says which is still missing rather than leaving the
 *     operator to guess at a greyed button.
 *   - There is no confirm dialog that resolves to an outcome. The confirmation
 *     is the consequence text, in place, before the click.
 *
 * # The result is a panel and never a toast
 *
 * An allowance can start a countdown on an address somebody is being paid to —
 * the response names the displaced handle and the instant it stops reaching
 * them — and a snackbar takes that away after four seconds. So the answer
 * replaces the controls in place and stays there.
 *
 * It is also why nothing here re-reads the queue. A refetch would unmount this
 * panel and take the two fields with it, and the row is gone from the queue
 * either way: no endpoint in this backend returns a decided review, so there is
 * nothing to re-read it AS. The panel says the list above is now stale instead
 * of quietly repairing it — a queue that silently rearranges itself under an
 * operator mid-decision is worse than one that says it is out of date.
 *
 * # Not a `composer`
 *
 * No amount, no currency, no idempotency key, no posting preview. This block
 * decides a NAME: no money moves, no `decision_ref` is minted, and law 14's
 * four-outcome vocabulary is a different closed set that must not appear here.
 */

interface Props {
  readonly block: DecideBlock
  /** Standby cell, or a role this screen is not scoped to — law 18/19. */
  readonly writeBlocked: boolean
  readonly writeNote: string
}

export function Decide({ block, writeBlocked, writeNote }: Props) {
  /** `''` is "the operator has not chosen", and it is the only start state. */
  const [outcome, setOutcome] = useState('')
  const [code, setCode] = useState('')
  const [note, setNote] = useState('')
  const [inFlight, setInFlight] = useState(false)
  const [panel, setPanel] = useState<DecidePanel | null>(null)

  const unwired = block.submit === undefined
  // Three independent reasons the button may be dead, and they are reported in
  // the order an operator can act on: the shell's posture first (nothing they
  // type will help), then what the endpoint can do, then what they have not
  // filled in yet.
  const shellBlock = writeBlocked
    ? writeNote
    : block.blocked !== ''
      ? block.blocked
      : unwired
        ? 'This console cannot submit this decision: no endpoint is wired behind the control.'
        : ''

  const chosen = block.outcomes.find((o) => o[0] === outcome)
  const offered = block.reasons.filter((r) => r[2] === '' || r[2] === outcome)
  const reasonBad = reasonRefusal(code, note)

  const missing = chosen === undefined
    ? 'Choose an outcome. There is no default — a decision nobody made is not a decision.'
    : reasonBad ?? ''

  const stop = shellBlock !== '' ? shellBlock : missing
  const decided = panel !== null && panel.final
  const disabled = stop !== '' || inFlight || decided

  /**
   * Whether the INPUTS are dead, which is a narrower question than whether the
   * button is.
   *
   * Only the shell's refusals freeze the fields — a standby cell, a missing
   * permission, an unwired endpoint. "You have not chosen a reason yet" must
   * not, or the operator could never choose one: that refusal belongs on the
   * button and on the sentence beside it, never on the field that answers it.
   */
  const frozen = shellBlock !== '' || inFlight

  async function submit() {
    if (block.submit === undefined || chosen === undefined || reasonBad !== null) return
    setInFlight(true)
    // `submit` never rejects — see `DecideSubmit`. Every answer the backend can
    // give is a panel, so there is no catch that could swallow one.
    const answer = await block.submit(chosen[0], serialiseReason(code, note))
    setInFlight(false)
    setPanel(answer)
  }

  return (
    <div
      style={{
        border: '1px solid var(--k-line)',
        borderTop: '2px solid var(--k-rule)',
        background: 'var(--k-panel)',
        maxWidth: '900px',
      }}
    >
      <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
        <div style={{ fontSize: '12.5px', fontWeight: 600 }}>{block.lead}</div>
        <div
          style={{
            fontSize: '11.5px',
            color: 'var(--k-mut)',
            marginTop: '3px',
            maxWidth: '78ch',
            textWrap: 'pretty',
          }}
        >
          {block.body}
        </div>
      </div>

      {panel !== null && <ResultPanel panel={panel} />}

      {!decided && (
        <>
          <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
            <div style={FIELD_LABEL}>Outcome — required, and there is no default</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '2px' }}>
              {block.outcomes.map(([value, label, consequence, tone]) => (
                <label
                  key={value}
                  data-t={tone}
                  data-cur={outcome === value ? '1' : ''}
                  style={{
                    flex: '1 1 260px',
                    display: 'block',
                    border: '1px solid var(--k-line)',
                    borderLeft: `3px solid var(--k-${tone})`,
                    background: outcome === value ? 'var(--k-panel2)' : 'var(--k-bg)',
                    padding: '8px 10px',
                    cursor: frozen ? 'default' : 'pointer',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <input
                      type="radio"
                      name={`decide-${block.lead}`}
                      value={value}
                      checked={outcome === value}
                      disabled={frozen}
                      onChange={() => {
                        setOutcome(value)
                        // The chosen code is cleared, never carried across. The
                        // codes are filtered by outcome precisely so an
                        // allowance cannot be recorded under a refusal's reason,
                        // and a sticky selection would defeat that silently.
                        setCode('')
                      }}
                    />
                    <span style={{ fontSize: '12.5px', fontWeight: 700 }}>{label}</span>
                  </span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: '11.5px',
                      color: 'var(--k-mut)',
                      marginTop: '4px',
                      textWrap: 'pretty',
                    }}
                  >
                    {consequence}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ padding: '11px 12px', borderBottom: '1px solid var(--k-line)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'flex-end' }}>
              <div>
                <div style={FIELD_LABEL}>Reason code — required</div>
                <select
                  aria-label="Reason code"
                  value={code}
                  disabled={frozen || chosen === undefined}
                  onChange={(e) => setCode(e.target.value)}
                  style={{
                    height: '28px',
                    minWidth: '250px',
                    border: '1px solid var(--k-line)',
                    background: 'var(--k-bg)',
                    color: 'var(--k-ink)',
                    font: 'inherit',
                    fontSize: '12px',
                    padding: '0 6px',
                    borderRadius: 0,
                  }}
                >
                  {/* An unselectable placeholder, not a first real code. A
                      select whose first option is already a valid answer is a
                      default, and this control may not have one. */}
                  <option value="">
                    {chosen === undefined ? '— choose an outcome first' : '— choose a reason code'}
                  </option>
                  {offered.map(([value, gloss]) => (
                    <option key={value} value={value}>
                      {`${value} — ${gloss}`}
                    </option>
                  ))}
                </select>
                <div style={FIELD_NOTE}>{block.reasonNote}</div>
              </div>

              <div style={{ flex: 1, minWidth: '240px' }}>
                <div style={FIELD_LABEL}>Note — optional, goes in the same field</div>
                <input
                  aria-label="Reason note"
                  value={note}
                  disabled={frozen || chosen === undefined}
                  onChange={(e) => setNote(e.target.value)}
                  style={{
                    height: '28px',
                    width: '100%',
                    border: '1px solid var(--k-line)',
                    background: 'var(--k-bg)',
                    color: 'var(--k-ink)',
                    font: 'inherit',
                    fontSize: '12px',
                    padding: '0 8px',
                    borderRadius: 0,
                  }}
                />
                <div style={{ ...FIELD_NOTE, fontFamily: 'var(--k-mono)' }}>
                  {/* Bytes, because the server's cap is Go's len() on a string
                      and that is a byte count. A counter reading characters
                      would show room left on a Cyrillic note the server then
                      refuses — see `reasoncode.ts`. */}
                  {`${String(reasonRemaining(code, note))} of 200 bytes left · sent as ${JSON.stringify(serialiseReason(code, note))}`}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              padding: '11px 12px',
              background: 'var(--k-bg)',
            }}
          >
            <button
              type="button"
              disabled={disabled}
              data-dim={disabled ? '1' : ''}
              onClick={() => void submit()}
              style={{
                height: '29px',
                padding: '0 13px',
                border: 0,
                background: 'var(--k-accent)',
                color: 'var(--k-accentink)',
                font: 'inherit',
                fontWeight: 800,
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {inFlight ? 'Submitting…' : block.cta}
            </button>
            <span style={{ fontSize: '11.5px', color: 'var(--k-mut)', maxWidth: '60ch' }}>
              {stop === '' ? block.ctaNote : stop}
            </span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '11px',
                color: 'var(--k-mut2)',
                fontFamily: 'var(--k-mono)',
              }}
            >
              {block.auditLine}
            </span>
          </div>
        </>
      )}

      <div style={{ padding: '0 12px 11px', fontSize: '11px', color: 'var(--k-mut2)' }}>
        {writeNote}
      </div>
    </div>
  )
}

function ResultPanel({ panel }: { readonly panel: DecidePanel }) {
  return (
    <div
      data-t={panel.tone}
      style={{
        borderBottom: '1px solid var(--k-line)',
        borderLeft: `3px solid var(--k-${panel.tone})`,
        background: 'var(--k-panel2)',
        padding: '11px 12px',
      }}
    >
      <div style={{ fontSize: '12.5px', fontWeight: 700 }}>{panel.heading}</div>
      {panel.lines.map(([label, value, hint]) => (
        <div key={label} style={{ display: 'flex', gap: '10px', marginTop: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: 'var(--k-mut2)', minWidth: '170px' }}>
            {label}
          </span>
          <span style={{ fontSize: '12px', fontFamily: 'var(--k-mono)', fontWeight: 600 }}>
            {value}
          </span>
          {hint !== '' && (
            <span style={{ fontSize: '11px', color: 'var(--k-mut)' }}>{hint}</span>
          )}
        </div>
      ))}
      <div
        style={{
          fontSize: '11.5px',
          color: 'var(--k-mut)',
          marginTop: '8px',
          maxWidth: '78ch',
          textWrap: 'pretty',
        }}
      >
        {panel.note}
      </div>
    </div>
  )
}

const FIELD_LABEL = {
  fontSize: '10px',
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'var(--k-mut2)',
  marginBottom: '4px',
} as const

const FIELD_NOTE = {
  fontSize: '11px',
  color: 'var(--k-mut)',
  marginTop: '4px',
} as const

